import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { Loader2, Save, Ticket, DollarSign, Trophy, Megaphone, Calendar, MapPin, UserPlus, Wallet, Award, KeyRound, Banknote } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ShareMenu from "@/components/ShareMenu";
import { BoardShareImage } from "@/components/BoardShareImage";
import { Logo } from "@/components/Logo";
import { formatDate } from "@/components/EventCard";

export default function MemberDashboard() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [sales, setSales] = useState(null);
  const [events, setEvents] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [board, setBoard] = useState([]);
  const [contest, setContest] = useState(null);
  const [pw, setPw] = useState({ current: "", next: "" });
  const [saving, setSaving] = useState(false);

  const loadWallet = useCallback(() => {
    api.get("/wallet/me").then((r) => setWallet(r.data)).catch(() => {});
    api.get("/wallet/payouts").then((r) => setPayouts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
    if (user) {
      setForm({ nickname: user.nickname || "", name: user.name || "", rides: user.rides || "", bio: user.bio || "", photo: user.photo || "", instagram: user.socials?.instagram || "" });
      api.get("/members/me/sales").then((r) => setSales(r.data)).catch(() => {});
      api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
      api.get("/announcements").then((r) => setAnnouncements(r.data)).catch(() => {});
      api.get("/leaderboard").then((r) => setBoard(r.data)).catch(() => {});
      api.get("/contests/active").then((r) => setContest(r.data && r.data.contest ? r.data : null)).catch(() => {});
      loadWallet();
    }
  }, [user, loading, navigate, loadWallet]);

  if (loading || !user || !form) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-[#e0481f]" /></div>;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const origin = window.location.origin;
  const refUrl = `${origin}/events?ref=${user.ref_code}`;
  const joinUrl = `${origin}/join?ref=${user.ref_code}`;
  const boardUrl = `${origin}/board?ref=${user.ref_code}`;
  const nowIso = new Date().toISOString();
  const upcoming = events.filter((e) => (e.date || "") >= nowIso).slice(0, 6);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/me", { nickname: form.nickname, name: form.name, rides: form.rides, bio: form.bio, photo: form.photo, socials: { instagram: form.instagram } });
      setUser(data);
      toast.success("Profile updated!");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const cashOut = async () => {
    try {
      await api.post("/wallet/cashout", {});
      toast.success("Cash-out requested! Admin will process your payout.");
      loadWallet();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const changePw = async () => {
    if (!pw.current || !pw.next) { toast.error("Fill in both password fields"); return; }
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password updated!");
      setPw({ current: "", next: "" });
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10 space-y-7">
      <div className="flex items-center gap-4">
        <Logo className="h-14" />
        <div>
          <p className="eyebrow text-[#e0481f] text-xs">Member Portal</p>
          <h1 className="headline text-4xl text-[#f7f3ec]">{user.nickname}</h1>
        </div>
      </div>

      {/* Login & password */}
      <div className="card-warm rounded-2xl p-6" data-testid="change-password">
        <h2 className="headline text-2xl text-[#f7f3ec] mb-1 flex items-center gap-2"><KeyRound size={20} className="text-[#e0481f]" /> Login & Password</h2>
        <p className="text-[#a9a196] text-sm mb-4">You log in with <span className="text-[#f3c649]">{user.email}</span>. Change your password anytime.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} placeholder="Current password" className="inp" data-testid="pw-current" />
          <input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} placeholder="New password" className="inp" data-testid="pw-new" />
        </div>
        <button onClick={changePw} className="mt-3 btn-ember rounded-full px-6 py-3 font-display uppercase tracking-wide inline-flex items-center gap-2" data-testid="pw-save"><KeyRound size={16} /> Update Password</button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        <Stat icon={DollarSign} label="Revenue Generated" value={`$${(sales?.total_revenue || 0).toFixed(2)}`} />
        <Stat icon={Ticket} label="Tickets Sold" value={sales?.tickets_sold || 0} />
        <Stat icon={Trophy} label="Ref Code" value={user.ref_code} />
      </div>

      {/* Active sales contest */}
      {contest && (
        <div className="card-warm rounded-2xl p-6 border border-[#e0481f]/40" data-testid="contest-banner">
          <p className="eyebrow text-[#e0481f] text-xs mb-1">Active Sales Contest</p>
          <h2 className="headline text-3xl text-[#f7f3ec]">{contest.contest.name}</h2>
          {contest.contest.prize && <p className="text-[#f3c649] mb-3">Prize: {contest.contest.prize}</p>}
          <div className="space-y-2">
            {contest.leaderboard.slice(0, 5).map((b) => (
              <div key={b.member_id} className={`flex justify-between items-center rounded-xl px-4 py-2.5 ${b.ref_code === user.ref_code ? "bg-[#d4af37]/15 border border-[#d4af37]/40" : "bg-[#1a1816]"}`}>
                <span className="text-[#f7f3ec]"><span className="headline text-lg mr-2 gold-text">#{b.rank}</span>{b.nickname}{b.ref_code === user.ref_code && <span className="text-[#f3c649] text-xs"> (You)</span>}</span>
                <span className="text-[#a9a196] text-sm">{b.tickets} tix • ${b.revenue.toFixed(2)}</span>
              </div>
            ))}
            {contest.leaderboard.length === 0 && <p className="text-[#8f857a] text-sm">No sales yet in this contest — get out there!</p>}
          </div>
        </div>
      )}

      {/* Wallet + Leaderboard */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-warm rounded-2xl p-6" data-testid="wallet-card">
          <h2 className="headline text-2xl text-[#f7f3ec] mb-1 flex items-center gap-2"><Wallet size={22} className="text-[#e0481f]" /> My Wallet</h2>
          <p className="text-[#a9a196] text-sm mb-2">You earn <span className="text-[#f3c649]">{wallet?.commission_percent ?? 15}%</span> on every ticket sold. {wallet?.next_tier ? `Sell $${Math.max(0, (wallet.next_tier.min_sales - (wallet.gross_sales || 0))).toFixed(0)} more to unlock ${wallet.next_tier.percent}%.` : "You're at the top tier!"}</p>
          <p className="text-[#8f857a] text-xs mb-4">Balance is spendable on gear in the <a href="/shop" className="text-[#f3c649]">Shop</a>, or cash out (admin pays you your way).</p>
          <div className="bg-[#1a1816] rounded-xl p-5 mb-4 text-center">
            <p className="eyebrow text-[#d4af37] text-xs mb-1">Available To Cash Out</p>
            <p className="headline text-5xl gold-text" data-testid="wallet-available">${(wallet?.available ?? 0).toFixed(2)}</p>
          </div>
          <div className="grid grid-cols-3 gap-2 mb-4 text-center">
            <div className="bg-[#1a1816] rounded-lg p-3"><p className="headline text-lg text-[#f7f3ec]">${(wallet?.earned ?? 0).toFixed(2)}</p><p className="text-[#8f857a] text-[10px] uppercase tracking-wide">Earned</p></div>
            <div className="bg-[#1a1816] rounded-lg p-3"><p className="headline text-lg text-[#f7f3ec]">${(wallet?.pending ?? 0).toFixed(2)}</p><p className="text-[#8f857a] text-[10px] uppercase tracking-wide">Pending</p></div>
            <div className="bg-[#1a1816] rounded-lg p-3"><p className="headline text-lg text-[#f7f3ec]">${(wallet?.paid_out ?? 0).toFixed(2)}</p><p className="text-[#8f857a] text-[10px] uppercase tracking-wide">Paid Out</p></div>
          </div>
          <button onClick={cashOut} disabled={!wallet || wallet.available <= 0} className="w-full btn-gold rounded-full py-3 font-display uppercase tracking-wide inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="cash-out"><Banknote size={18} /> Cash Out</button>
          {payouts.length > 0 && (
            <div className="mt-4 space-y-1.5">
              {payouts.map((p) => (
                <div key={p.id} className="flex justify-between items-center text-xs bg-[#1a1816] rounded-lg px-3 py-2">
                  <span className="text-[#a9a196]">{formatDate(p.created_at)}</span>
                  <span className="text-[#f7f3ec]">${p.amount.toFixed(2)}</span>
                  <span className={p.status === "paid" ? "text-[#4ade80]" : p.status === "rejected" ? "text-[#e0481f]" : "text-[#d4af37]"}>{p.status}</span>
                </div>
              ))}
            </div>
          )}
          <p className="text-[10px] text-[#6b645b] text-center mt-3">Payouts are reviewed & paid by an admin — your way (cash, Zelle, etc.)</p>
        </div>

        <div className="card-warm rounded-2xl p-6" data-testid="leaderboard">
          <h2 className="headline text-2xl text-[#f7f3ec] mb-4 flex items-center gap-2"><Award size={22} className="text-[#d4af37]" /> Seller Leaderboard</h2>
          {board.length ? (
            <div className="space-y-2">
              {board.map((b) => (
                <div key={b.member_id} className={`flex items-center justify-between rounded-xl px-4 py-3 ${b.ref_code === user.ref_code ? "bg-[#d4af37]/15 border border-[#d4af37]/40" : "bg-[#1a1816]"}`}>
                  <div className="flex items-center gap-3">
                    <span className={`headline text-xl ${b.rank === 1 ? "gold-text" : "text-[#8f857a]"}`}>#{b.rank}</span>
                    <span className="text-[#f7f3ec]">{b.nickname}{b.ref_code === user.ref_code && <span className="text-[#f3c649] text-xs"> (You)</span>}</span>
                  </div>
                  <div className="text-right"><p className="headline text-base text-[#f7f3ec]">{b.tickets} tix</p><p className="text-[#8f857a] text-xs">${b.revenue.toFixed(2)}</p></div>
                </div>
              ))}
            </div>
          ) : <p className="text-[#8f857a]">No sales ranked yet. Be the first to sell!</p>}
        </div>
      </div>

      {/* QR + referral link + recruit */}
      <div className="grid sm:grid-cols-2 gap-6">
        <div className="card-warm rounded-2xl p-6">
          <h2 className="headline text-2xl text-[#f7f3ec] mb-1">My Ticket Link & QR</h2>
          <p className="text-[#a9a196] text-sm mb-4">Every ticket bought through this is credited to you.</p>
          <div className="flex flex-col items-center gap-4">
            <div className="bg-white p-3 rounded-xl" data-testid="member-qr"><QRCodeCanvas value={refUrl} size={140} /></div>
            <div className="w-full bg-[#1a1816] rounded-xl px-4 py-3 font-mono text-xs text-[#f3c649] break-all text-center">{refUrl}</div>
          </div>
          <div className="mt-4"><ShareMenu url={refUrl} label="Share your ticket link" /></div>
        </div>

        <div className="card-warm rounded-2xl p-6">
          <h2 className="headline text-2xl text-[#f7f3ec] mb-1 flex items-center gap-2"><UserPlus size={22} className="text-[#e0481f]" /> Recruit The Family</h2>
          <p className="text-[#a9a196] text-sm mb-4">Invite riders to join the I-65 Ryders. Your name rides along with the invite.</p>
          <div className="flex flex-col items-center gap-4">
            <div className="bg-white p-3 rounded-xl"><QRCodeCanvas value={joinUrl} size={140} /></div>
            <div className="w-full bg-[#1a1816] rounded-xl px-4 py-3 font-mono text-xs text-[#f3c649] break-all text-center">{joinUrl}</div>
          </div>
          <div className="mt-4"><ShareMenu url={joinUrl} label="Share the invite" /></div>
        </div>

        <div className="card-warm rounded-2xl p-6 sm:col-span-2" data-testid="member-board-link">
          <h2 className="headline text-2xl text-[#f7f3ec] mb-1">My Square Board Link</h2>
          <p className="text-[#a9a196] text-sm mb-4">Send this to customers. Every square bought through it is credited to you.</p>
          <div className="flex flex-col items-center gap-4">
            <div className="bg-white p-3 rounded-xl"><QRCodeCanvas value={boardUrl} size={140} /></div>
            <div className="w-full bg-[#1a1816] rounded-xl px-4 py-3 font-mono text-xs text-[#f3c649] break-all text-center" data-testid="member-board-url">{boardUrl}</div>
          </div>
          <div className="mt-4"><ShareMenu url={boardUrl} label="Share the square board" /></div>
          <div className="mt-6 pt-5 border-t border-[#3a332c]">
            <h3 className="headline text-lg text-[#f7f3ec] mb-1">Board Image For Social Media</h3>
            <p className="text-[#a9a196] text-sm mb-3">A picture of the live board with your link printed on it, ready for Facebook and Instagram.</p>
            <BoardShareImage link={boardUrl} />
          </div>
        </div>
      </div>

      {/* Announcements */}
      {announcements.length > 0 && (
        <div className="card-warm rounded-2xl p-6" data-testid="member-announcements">
          <h2 className="headline text-2xl text-[#f7f3ec] mb-4 flex items-center gap-2"><Megaphone size={20} className="text-[#d4af37]" /> Member Announcements</h2>
          <div className="space-y-3">
            {announcements.map((a) => (
              <div key={a.id} className="bg-[#1a1816] rounded-xl p-4">
                <p className="headline text-lg text-[#f7f3ec]">{a.title}</p>
                <p className="text-[#d6cec2] text-sm">{a.body}</p>
                <p className="text-[#6b645b] text-xs mt-1">{formatDate(a.created_at)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Promote upcoming events */}
      <div className="card-warm rounded-2xl p-6" data-testid="promote-events">
        <h2 className="headline text-2xl text-[#f7f3ec] mb-1">Events To Promote</h2>
        <p className="text-[#a9a196] text-sm mb-4">Share these — sales through your link get credited to you.</p>
        {upcoming.length ? (
          <div className="space-y-4">
            {upcoming.map((e) => (
              <div key={e.id} className="bg-[#1a1816] rounded-xl p-4" data-testid={`promote-${e.slug}`}>
                <div className="flex items-center gap-4 mb-3">
                  <img src={e.banner_image || e.flyer_image} alt={e.title} className="w-16 h-16 rounded-lg object-cover" />
                  <div>
                    <h3 className="headline text-xl text-[#f7f3ec]">{e.title}</h3>
                    <p className="text-[#a9a196] text-sm flex items-center gap-3"><span className="flex items-center gap-1"><Calendar size={13} className="text-[#e0481f]" /> {formatDate(e.date)}</span><span className="flex items-center gap-1"><MapPin size={13} className="text-[#e0481f]" /> {e.location}</span></p>
                  </div>
                </div>
                <ShareMenu url={`${origin}/events/${e.slug}?ref=${user.ref_code}`} compact />
              </div>
            ))}
          </div>
        ) : <p className="text-[#8f857a]">No upcoming events to promote yet.</p>}
      </div>

      {/* Profile editor */}
      <div className="card-warm rounded-2xl p-6">
        <h2 className="headline text-2xl text-[#f7f3ec] mb-4">Edit My Profile</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Rider Name / Nickname"><input value={form.nickname} onChange={set("nickname")} className="inp" data-testid="dash-nickname" /></Field>
          <Field label="What You Ride"><input value={form.rides} onChange={set("rides")} className="inp" data-testid="dash-rides" /></Field>
          <Field label="Photo URL"><input value={form.photo} onChange={set("photo")} placeholder="https://..." className="inp" data-testid="dash-photo" /></Field>
          <Field label="Instagram URL"><input value={form.instagram} onChange={set("instagram")} placeholder="https://instagram.com/..." className="inp" data-testid="dash-instagram" /></Field>
          <div className="sm:col-span-2"><Field label="Short Bio"><textarea value={form.bio} onChange={set("bio")} rows={3} className="inp resize-none" data-testid="dash-bio" /></Field></div>
        </div>
        <button onClick={save} disabled={saving} className="mt-4 btn-gold rounded-full px-6 py-3 font-display uppercase tracking-wide inline-flex items-center gap-2" data-testid="dash-save">
          {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Save Profile
        </button>
      </div>

      {/* My sales */}
      <div className="card-warm rounded-2xl p-6">
        <h2 className="headline text-2xl text-[#f7f3ec] mb-4">Your Sales</h2>
        {sales?.orders?.length ? (
          <div className="space-y-2">
            {sales.orders.map((o) => (
              <div key={o.id} className="flex justify-between items-center bg-[#1a1816] rounded-xl px-4 py-3 text-sm">
                <div><p className="text-[#f7f3ec]">{o.customer_name}</p><p className="text-[#8f857a] text-xs">{o.event_title} • {o.ticket_type} × {o.quantity}</p></div>
                <div className="text-right"><p className="headline text-lg gold-text">${o.amount_paid}</p><p className="text-[#8f857a] text-xs">{formatDate(o.created_at)}</p></div>
              </div>
            ))}
          </div>
        ) : <p className="text-[#8f857a]">No sales yet. Share your link to start earning credit!</p>}
      </div>
      <style>{`.inp{width:100%;background:#1a1816;border:1px solid #3a332c;border-radius:12px;padding:10px 14px;color:#f7f3ec;outline:none}.inp:focus{border-color:#e0481f}`}</style>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="card-warm rounded-2xl p-4 sm:p-5">
      <Icon className="text-[#e0481f] mb-2" size={20} />
      <p className="headline text-xl sm:text-2xl text-[#f7f3ec] break-all">{value}</p>
      <p className="text-[#8f857a] text-[10px] sm:text-xs uppercase tracking-wide">{label}</p>
    </div>
  );
}
function Field({ label, children }) {
  return <div><label className="eyebrow text-xs text-[#d4af37] block mb-1.5">{label}</label>{children}</div>;
}
